// src/pages/Invoices/Report/SalesItemsSummary.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { Autocomplete, TextField, Typography } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import axios from "../../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

const moneyCell = (color) => (p) => (
  <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 14, color }}>{fmtMoney(p.value)}</Typography>
);

export default function SalesItemsSummary() {
  const { t } = useTranslation();
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const range = useReportDateRange("today");
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);

  const [branchId, setBranchId] = useState("");
  const [stockId, setStockId] = useState("");
  const [product, setProduct] = useState(null);

  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data || [])).catch(() => {});
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data || [])).catch(() => {});
  }, [companyId]);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const fetchReport = useCallback(async () => {
    if (!companyId) {
      toast.error(t("common.companyIdRequired", "رقم الشركة غير موجود"));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get("/Invoice/SalesItemsSummary", {
        params: {
          companyId,
          branchId: branchId ? Number(branchId) : 0,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          stockId: stockId || null,
          productId: product?.productId || null,
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data.map((r, i) => ({ id: i + 1, ...r })));
    } catch {
      toast.error(t("invoicesReport.errorFetchingReport", "فشل تحميل التقرير"));
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, stockId, product, range.dateFrom, range.dateTo, t]);

  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    qty: a.qty + (Number(r.totalQuantity) || 0),
    gross: a.gross + (Number(r.grossSales) || 0),
    discount: a.discount + (Number(r.totalDiscount) || 0),
    vat: a.vat + (Number(r.totalVAT) || 0),
    net: a.net + (Number(r.netSales) || 0),
  }), { qty: 0, gross: 0, discount: 0, vat: 0, net: 0 }), [rows]);

  const columns = useMemo(() => [
    { field: "productId", headerName: "الرقم", width: 80, headerAlign: "center", align: "center" },
    {
      field: "productNameAr", headerName: t("invoice.fields.ProductId", "الصنف"),
      flex: 1.6, minWidth: 200, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{p.value}</Typography>,
    },
    {
      field: "totalQuantity", headerName: t("invoice.fields.Quantity", "الكمية"),
      type: "number", flex: 1, minWidth: 100, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 14 }}>{fmtNumber(p.value)}</Typography>,
    },
    { field: "grossSales", headerName: t("invoicesReport.columns.total", "الإجمالي"), type: "number", flex: 1, minWidth: 120, headerAlign: "center", align: "center", renderCell: moneyCell("#0f172a") },
    { field: "totalDiscount", headerName: t("invoicesReport.columns.discount", "الخصم"), type: "number", flex: 1, minWidth: 110, headerAlign: "center", align: "center", renderCell: moneyCell("#e11d48") },
    { field: "totalVAT", headerName: t("invoicesReport.columns.tax", "الضريبة"), type: "number", flex: 1, minWidth: 110, headerAlign: "center", align: "center", renderCell: moneyCell("#d97706") },
    { field: "netSales", headerName: t("invoicesReport.columns.net", "الصافي"), type: "number", flex: 1, minWidth: 130, headerAlign: "center", align: "center", renderCell: moneyCell("#059669") },
  ], [t]);

  // ── التصدير ──
  const fileBase = `SalesItemsSummary_${range.dateFrom}_${range.dateTo}`;

  const exportExcel = () => {
    if (!rows.length) return toast.info("لا توجد بيانات للتصدير");
    const sheetRows = rows.map(r => ({
      "الرقم": r.productId,
      "الصنف": r.productNameAr,
      "الكمية": Number(r.totalQuantity) || 0,
      "الإجمالي": Number(r.grossSales) || 0,
      "الخصم": Number(r.totalDiscount) || 0,
      "الضريبة": Number(r.totalVAT) || 0,
      "الصافي": Number(r.netSales) || 0,
    }));
    sheetRows.push({
      "الرقم": "", "الصنف": "الإجمالي", "الكمية": totals.qty, "الإجمالي": totals.gross,
      "الخصم": totals.discount, "الضريبة": totals.vat, "الصافي": totals.net,
    });
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    ws["!cols"] = [{ wch: 8 }, { wch: 34 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];
    ws["!views"] = [{ RTL: true }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ملخص الأصناف");
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const exportPDF = () => {
    if (!rows.length) return toast.info("لا توجد بيانات للتصدير");
    const doc = new jsPDF("l", "pt", "a4");
    doc.text(`Sales Items Summary  ${range.dateFrom} - ${range.dateTo}`, 40, 30);
    doc.autoTable({
      // ✅ عدد الأعمدة يطابق عدد القيم (كان هناك عمود زائد يُزيح البيانات)
      head: [["ID", "Item", "Qty", "Total", "Discount", "VAT", "Net"]],
      body: [
        ...rows.map(r => [
          r.productId, r.productNameAr, fmtNumber(r.totalQuantity),
          fmtMoney(r.grossSales), fmtMoney(r.totalDiscount), fmtMoney(r.totalVAT), fmtMoney(r.netSales),
        ]),
        ["", "TOTAL", fmtNumber(totals.qty), fmtMoney(totals.gross), fmtMoney(totals.discount), fmtMoney(totals.vat), fmtMoney(totals.net)],
      ],
      startY: 45,
      styles: { fontSize: 9 },
    });
    doc.save(`${fileBase}.pdf`);
  };

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
    setStockId("");
    setProduct(null);
  };

  const bName = branchName(branchId);

  return (
    <ReportPage
      title={t("salesMenu.aggregatedItemsSalesReport", "ملخص مبيعات الأصناف")}
      icon={<Inventory2OutlinedIcon />}
      period={range}
      badges={bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []}
      actions={[
        { label: "Excel", icon: <GridOnIcon />, onClick: exportExcel },
        { label: "PDF", icon: <PictureAsPdfOutlinedIcon />, onClick: exportPDF, primary: true },
      ]}
      stats={[
        { label: "عدد الأصناف", value: fmtNumber(rows.length), icon: <CategoryOutlinedIcon />, color: "primary" },
        { label: "إجمالي الكميات", value: fmtNumber(totals.qty), icon: <ShoppingCartOutlinedIcon />, color: "slate" },
        { label: "إجمالي المبيعات", value: fmtMoney(totals.gross), icon: <AttachMoneyIcon />, color: "info" },
        { label: "الخصومات", value: fmtMoney(totals.discount), icon: <LocalOfferIcon />, color: "warning" },
        { label: "الضريبة", value: fmtMoney(totals.vat), icon: <ReceiptLongIcon />, color: "error" },
        { label: "الصافي", value: fmtMoney(totals.net), icon: <AccountBalanceWalletIcon />, color: "success", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={(branchId ? 1 : 0) + (stockId ? 1 : 0) + (product ? 1 : 0)}
        >
          <ReportSelect label={t("ledgerReport.branch", "الفرع")} value={branchId} onChange={setBranchId} options={branchOptions} />
          <ReportSelect
            label="المخزن"
            value={stockId}
            onChange={setStockId}
            options={stocks.map(s => ({ value: String(s.id), label: s.nameAr || s.name }))}
          />
          {/* قائمة بحث للأصناف — أسرع من قائمة منسدلة طويلة */}
          <Autocomplete
            size="small"
            options={products}
            value={product}
            onChange={(_, v) => setProduct(v)}
            getOptionLabel={(p) => p?.arabicName || p?.nameAr || ""}
            isOptionEqualToValue={(a, b) => a.productId === b.productId}
            noOptionsText="لا توجد نتائج"
            renderInput={(params) => (
              <TextField {...params} label={t("invoice.fields.ProductId", "الصنف")} placeholder="الكل" sx={fieldSx} />
            )}
          />
        </ReportFilters>
      }
    >
      <ReportTableCard title="الأصناف المباعة" count={fmtNumber(rows.length)}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "netSales", sort: "desc" }] },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          localeText={{
            noRowsLabel: "لا توجد مبيعات في هذه الفترة",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={reportGridSx}
        />
      </ReportTableCard>
    </ReportPage>
  );
}
